import {
  AbstractControl,
  FormGroupDirective,
  NgForm,
  ValidatorFn,
} from '@angular/forms';
import { ErrorStateMatcher } from '@angular/material/core';
import moment from 'moment-timezone';
import { DATE_INPUT_FORMAT, TIME_INPUT_FORMAT } from '../utils/input-formats';
import {
  enumerateSelectedDates,
  parseDate,
} from '../utils/schedule-dates.utils';
import { toAvailableSchedulePeriods } from '../utils/schedule-timezone.utils';

type RangeValue = { start: string; end: string };

// Time ('HH:mm') values sort lexicographically, so plain string comparison
// gives chronological order. Dates use `dateRangeOrderValidator` instead.
export const timeRangeOrderValidator = (): ValidatorFn => {
  return (control: AbstractControl<RangeValue>) => {
    const { start, end } = control.value;

    if (!start || !end) {
      return null;
    }

    return end <= start ? { endBeforeStart: true } : null;
  };
};

/** Allows the same start and end date. Invalid dates are left to `validDateValidator`. */
export const dateRangeOrderValidator = (): ValidatorFn => {
  return (control: AbstractControl<RangeValue>) => {
    const start = parseDate(control.value.start);
    const end = parseDate(control.value.end);

    if (!start.isValid() || !end.isValid()) {
      return null;
    }

    return end.isBefore(start, 'day') ? { endBeforeStart: true } : null;
  };
};

export const validDateValidator = (): ValidatorFn => {
  return (control: AbstractControl<string>) =>
    control.value && !parseDate(control.value).isValid()
      ? { invalidDate: true }
      : null;
};

/** `today` gives a `DATE_INPUT_FORMAT` date in the selected timezone. */
export const notBeforeTodayValidator = (today: () => string): ValidatorFn => {
  return (control: AbstractControl<string>) => {
    const date = parseDate(control.value);

    return date.isValid() && date.isBefore(parseDate(today()), 'day')
      ? { past: true }
      : null;
  };
};

/** `max` gives a `DATE_INPUT_FORMAT` date, inclusive. */
export const notAfterValidator = (max: () => string): ValidatorFn => {
  return (control: AbstractControl<string>) => {
    const date = parseDate(control.value);

    return date.isValid() && date.isAfter(parseDate(max()), 'day')
      ? { tooLate: true }
      : null;
  };
};

/** Filters days of the week down to those that currently count. */
type DaysFilter = (days: number[]) => number[];

/** `Validators.required`, counting only the days kept by `toVisibleDays`. */
export const visibleDaysRequiredValidator = (
  toVisibleDays: DaysFilter
): ValidatorFn => {
  return (control: AbstractControl<number[]>) =>
    toVisibleDays(control.value ?? []).length ? null : { required: true };
};

/**
 * Rejects a date range and days of the week that have no dates in common, so
 * the form can't submit nothing. Missing, invalid or out-of-order values are
 * left to the field and range validators.
 */
export const selectedDaysInRangeValidator = (
  toVisibleDays: DaysFilter
): ValidatorFn => {
  return (control: AbstractControl<{ date: RangeValue; days: number[] }>) => {
    const {
      date: { start, end },
    } = control.value;
    const days = toVisibleDays(control.value.days ?? []);

    if (control.get('date')?.invalid || !days.length) {
      return null;
    }

    return enumerateSelectedDates(start, end, days).length
      ? null
      : { noSelectedDaysInRange: true };
  };
};

/**
 * Rejects a selection where every selected time has already ended, as none
 * of it would be added. `now` is in the selected timezone. Missing, invalid
 * or out-of-order values are left to the field and range validators.
 */
export const notAllInPastValidator = (
  toVisibleDays: DaysFilter,
  now: () => moment.Moment
): ValidatorFn => {
  return (
    control: AbstractControl<{
      date: RangeValue;
      time: RangeValue;
      days: number[];
    }>
  ) => {
    const { date, time } = control.value;
    const days = toVisibleDays(control.value.days ?? []);

    if (control.get('date')?.invalid || control.get('time')?.invalid) {
      return null;
    }

    const current = now();
    const today = current.format(DATE_INPUT_FORMAT);

    const dates = enumerateSelectedDates(date.start, date.end, days).map(
      (selected) => selected.format(DATE_INPUT_FORMAT)
    );

    if (!dates.length || dates.some((date) => date > today)) {
      return null;
    }

    const currentTime = current.format(TIME_INPUT_FORMAT);

    if (dates.includes(today) && time.end > currentTime) {
      return null;
    }

    return { allInPast: true };
  };
};

/**
 * Rejects times the clocks going forward skip on every selected date, as none
 * of it would be added. `toDates` gives the selected dates from the control
 * in `DATE_INPUT_FORMAT`. Missing or out-of-order times and an invalid
 * timezone are left to their own validators.
 */
export const timesNotSkippedValidator = (
  toDates: (control: AbstractControl) => string[],
  timezoneName: () => string
): ValidatorFn => {
  return (control: AbstractControl) => {
    const time = control.get('time') as AbstractControl<RangeValue> | null;
    const zone = timezoneName();
    const selected = toDates(control);

    if (!time || time.invalid || !moment.tz.zone(zone) || !selected.length) {
      return null;
    }

    const times = [{ ...time.getRawValue(), isPreferred: false }];

    return selected.every(
      (date) => !toAvailableSchedulePeriods(date, times, zone).length
    )
      ? { timesSkipped: true }
      : null;
  };
};

/**
 * Puts a field into its error state when its parent group has the given
 * cross-field error, so every field in the group is flagged together.
 */
export class CrossFieldErrorStateMatcher implements ErrorStateMatcher {
  constructor(private readonly errorKey: string) {}

  isErrorState(
    control: AbstractControl | null,
    form: FormGroupDirective | NgForm | null
  ) {
    const group = control?.parent;
    const isInvalid = !!control?.invalid || !!group?.hasError(this.errorKey);
    const isInteracted =
      !!control?.touched || !!group?.touched || !!form?.submitted;

    return isInvalid && isInteracted;
  }
}
